import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';

import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

export interface PaginationProps {
  /** Zero-indexed current page. */
  pageIndex: number;
  /** Rows per page. */
  pageSize: number;
  /** Total rows across all pages. */
  totalRows: number;
  /** Options shown in the rows-per-page dropdown. Defaults to [25, 50, 100]. */
  pageSizeOptions?: number[];
  /** Called with the new zero-indexed page when the user navigates. */
  onPageChange: (pageIndex: number) => void;
  /** Called with the new page size when the user picks a different value. */
  onPageSizeChange: (pageSize: number) => void;
  className?: string;
}

const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  (
    { pageIndex, pageSize, totalRows, pageSizeOptions = [25, 50, 100], onPageChange, onPageSizeChange, className },
    ref,
  ) => {
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    const canPrev = pageIndex > 0;
    const canNext = pageIndex < totalPages - 1;

    const rangeLabel = (() => {
      if (totalRows === 0) return '0–0 of 0';
      const start = pageIndex * pageSize + 1;
      const end = Math.min((pageIndex + 1) * pageSize, totalRows);
      return `${start}–${end} of ${totalRows}`;
    })();

    return (
      <div
        ref={ref}
        className={cn(
          'tw:flex tw:items-center tw:justify-between tw:gap-6 tw:border-t tw:border-border tw:px-4 tw:py-2',
          className,
        )}
      >
        <div className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground">
          <span>Rows per page:</span>
          <Select value={String(pageSize)} onValueChange={(value) => onPageSizeChange(Number(value))}>
            <SelectTrigger size="small" className="tw:h-8 tw:w-[72px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground">
          <span>{rangeLabel}</span>
          <Button
            variant="outline"
            size="icon"
            className="tw:h-8 tw:w-8"
            disabled={!canPrev}
            onClick={() => onPageChange(pageIndex - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="tw:h-8 tw:w-8"
            disabled={!canNext}
            onClick={() => onPageChange(pageIndex + 1)}
            aria-label="Next page"
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    );
  },
);

Pagination.displayName = 'Pagination';

export { Pagination };
